'use client';

import { useOrganization } from '../Organization';
import { useAuthProvider } from '@modules/auth/providers/AuthProvider/AuthProvider';
import { useCommonAuthProvider } from '@modules/auth/providers/CommonAuthProvider/CommonAuthProvider';
import { useFlagsProxy } from '@modules/feature-flags/hooks/useFlagsProxy';
import { buildSandboxUrl, getSandboxInfo, resetSandboxInfo } from '@modules/sandboxes/utils/sandboxUtils';
import { useAnalytics } from '@modules/shared/analytics/hooks/useAnalytics';
import { CustomSpin } from '@modules/shared/components/CustomSpin';
import { isUserUnverified } from '@utils/user';

import * as Sentry from '@sentry/react';
import { Route } from 'next';
import { useRouter, usePathname } from 'next/navigation';
import React, { useState } from 'react';

const PAGES_WITHOUT_VALIDATION: { pathname: string }[] = [
  { pathname: '/404' },
  { pathname: '/signup' },
  { pathname: '/callback' },
  { pathname: '/auth0' },
  { pathname: '/authorize' },
  { pathname: '/logout' },
  { pathname: '/invite' },
];

export const RedirectProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const router = useRouter();
  const pathname = usePathname();
  const { organizationId } = useOrganization();
  const analytics = useAnalytics({ organizationId });
  const { loginWithRedirect, user, isLoading: isAuthLoading, hasIdpManagedProfile } = useCommonAuthProvider();
  const { authorizedOrganizationId } = useAuthProvider();
  const { cloudControlPlaneCdnUi } = useFlagsProxy();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  React.useEffect(() => {
    if (isAuthLoading) {
      return;
    }

    analytics.page();
  }, [pathname, user, isAuthLoading, authorizedOrganizationId]);

  React.useLayoutEffect(() => {
    if (!user || !user.sub || isAuthLoading) {
      return;
    }

    if (!pathname || PAGES_WITHOUT_VALIDATION.find((path) => path.pathname === pathname)) {
      setIsLoading(false);

      return;
    }

    // Redirect to the new subdomain if the flag is enabled
    if (cloudControlPlaneCdnUi && window.location.hostname === `manage.${process.env.SHARED_DOMAIN}`) {
      const newUrl = window.location.href.replace(
        `manage.${process.env.SHARED_DOMAIN}`,
        `app.${process.env.SHARED_DOMAIN}`,
      );

      Sentry.withScope((scope) => {
        scope.setExtras({
          originalUrl: window.location.href,
          targetUrl: newUrl,
          userId: user.sub,
          organizationId: authorizedOrganizationId,
        });
        Sentry.captureMessage('Redirecting to new subdomain URL');
      });

      loginWithRedirect({
        returnTo: window.location.pathname,
        redirect_uri: `https://app.${process.env.SHARED_DOMAIN}/callback`,
      });

      return;
    }

    if (pathname !== '/onboarding/profile') {
      const shouldRedirectToProfileOnboarding = !hasIdpManagedProfile && !user['https://cribl.cloud/completedProfile'];

      if (shouldRedirectToProfileOnboarding) {
        router.push('/onboarding/profile' as Route);

        return;
      }

      if (isUserUnverified(user.sub, Boolean(user.email_verified)) && pathname !== '/onboarding/verify') {
        const searchParams = new URLSearchParams(window.location.search);
        router.push(`/onboarding/verify?${searchParams.toString()}` as Route);

        return;
      }
    }

    const sandbox = getSandboxInfo();

    if (authorizedOrganizationId && sandbox) {
      const cloudURL = `${window.location.origin}/organizations/${authorizedOrganizationId}/workspaces/main`;
      const sandboxRedirectURL: string = buildSandboxUrl({
        sandboxInfo: sandbox,
        cloudUrl: cloudURL,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        allowedCallbackUrlsRegexList: [process.env.CRIBL_SANDBOX_ALLOWED_CALLBACK_URLS_REGEX!],
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        defaultCallbackUrl: process.env.CRIBL_SANDBOX_DEFAULT_CALLBACK_URL!,
        userEmail: user.email,
      });

      resetSandboxInfo();
      window.location.assign(sandboxRedirectURL);

      return;
    }

    setIsLoading(false);
  }, [user, isAuthLoading, cloudControlPlaneCdnUi, pathname]);

  if (isLoading) {
    return <CustomSpin />;
  }

  return <>{children}</>;
};
